import type { CaptureContext } from '@sentry/core';
import type { GraphQLFormattedError } from 'graphql';
import { removeToken } from '../../utils';
import { ErrorsService } from '../ErrorsService';

const SENSITIVE_KEYS = new Set([
  'password',
  'confirmPassword',
  'currentPassword',
  'newPassword',
  'newPasswordConfirm',
  'token',
  'authorization',
]);

export const sanitizeVariables = (variables: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(variables).map(([key, value]) => {
      if (SENSITIVE_KEYS.has(key)) return [key, '[REDACTED]'];
      if (value instanceof File || value instanceof Blob)
        return [key, `[File: ${(value as File).name ?? 'upload'}]`];
      if (Array.isArray(value))
        return [
          key,
          value.map(item =>
            item && typeof item === 'object'
              ? sanitizeVariables(item as Record<string, unknown>)
              : item,
          ),
        ];
      if (value && typeof value === 'object')
        return [key, sanitizeVariables(value as Record<string, unknown>)];
      return [key, value];
    }),
  );

type ReportError = (message: string, captureContext?: Partial<CaptureContext>) => string;

type RecoverFromInvalidSessionArgs = {
  pathname?: string;
  search?: string;
  assign?: (url: string) => void;
};

type HandleApolloOperationErrorArgs = {
  graphQLErrors?: readonly GraphQLFormattedError[];
  networkError?: unknown;
  operationName: string;
  variables?: Record<string, unknown>;
  reportError?: ReportError;
  recoverInvalidSession?: (args?: RecoverFromInvalidSessionArgs) => void;
  logNetworkError?: (message?: unknown, ...optionalParams: unknown[]) => void;
};

const AUTH_GRAPHQL_MESSAGES = new Set(['Not authenticated']);
const AUTH_EXTENSION_CODES = new Set(['UNAUTHENTICATED']);
const AUTH_REDIRECT_EXCLUDED_PATHS = [
  '/login',
  '/logout/',
  '/oauth/callback/',
  '/authenticate-by-link/',
];

let authRedirectInFlight = false;

const isAuthenticationGraphQLFormattedError = (
  error: Pick<GraphQLFormattedError, 'message' | 'extensions'>,
) => {
  return (
    AUTH_GRAPHQL_MESSAGES.has(error.message) ||
    AUTH_EXTENSION_CODES.has(String(error.extensions?.code ?? ''))
  );
};

const isUnauthorizedNetworkError = (networkError?: unknown) => {
  if (!networkError || typeof networkError !== 'object') {
    return false;
  }

  const statusCode = Reflect.get(networkError, 'statusCode');
  const status = Reflect.get(networkError, 'status');

  return statusCode === 401 || status === 401;
};

export const buildLoginRedirectPath = (pathname: string, search = '') => {
  const redirectTo = `${window.location.origin}${pathname}${search}`;
  const params = new URLSearchParams({ redirect_to: redirectTo });

  return `/login?${params.toString()}`;
};

export const recoverFromInvalidSession = ({
  pathname = window.location.pathname,
  search = window.location.search,
  assign = (url: string) => {
    window.location.href = url;
  },
}: RecoverFromInvalidSessionArgs = {}) => {
  removeToken();

  if (
    authRedirectInFlight ||
    AUTH_REDIRECT_EXCLUDED_PATHS.some(excludedPath => pathname.startsWith(excludedPath))
  ) {
    return;
  }

  authRedirectInFlight = true;
  assign(buildLoginRedirectPath(pathname, search));
};

export const handleApolloOperationError = ({
  graphQLErrors,
  networkError,
  operationName,
  variables = {},
  reportError = ErrorsService.message,
  recoverInvalidSession: recoverInvalidSessionHandler = recoverFromInvalidSession,
  logNetworkError = console.error,
}: HandleApolloOperationErrorArgs) => {
  let shouldRecoverSession = false;

  graphQLErrors?.forEach(({ message, path, extensions }) => {
    if (isAuthenticationGraphQLFormattedError({ message, extensions })) {
      shouldRecoverSession = true;
      return;
    }

    reportError(`[ApolloError ${path?.[0] ?? operationName ?? 'UnknownQuery'}]: ${message}`, {
      extra: {
        operationName,
        variables: sanitizeVariables(variables),
      },
    });
  });

  if (isUnauthorizedNetworkError(networkError)) {
    shouldRecoverSession = true;
  } else if (networkError) {
    logNetworkError(`[Network error]: ${networkError}`);
  }

  if (shouldRecoverSession) {
    recoverInvalidSessionHandler();
  }
};

export const resetAuthRedirectStateForTests = () => {
  authRedirectInFlight = false;
};
